import React, { useMemo } from 'react';
import {
  Stack,
  Typography,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssessmentIcon from '@mui/icons-material/Assessment';

import { HeroCard } from '../../../components/admin';

const getSystemStatus = (stats) => {
  if (!stats) {
    return {
      label: 'Loading system health...',
      tone: 'default',
      description: 'Fetching telemetry from cameras and alert services.',
    };
  }

  const cameraHealth = stats.camera_health || {};
  const totalCameras = cameraHealth.total ?? stats.cameras ?? 0;
  const onlineCameras = cameraHealth.online ?? 0;
  const awaitingCameras = cameraHealth.awaiting ?? 0;
  const offlineCameras = cameraHealth.offline ?? Math.max(totalCameras - onlineCameras - awaitingCameras, 0);
  const fallsToday = stats.fall_events?.today ?? 0;
  const openEnquiries = stats.contact_submissions?.open ?? 0;

  if (totalCameras === 0) {
    return {
      label: 'No cameras registered',
      tone: 'warning',
      description: 'Add at least one RTSP camera to begin continuous monitoring.',
    };
  }

  if (onlineCameras === 0) {
    if (awaitingCameras > 0) {
      return {
        label: 'Awaiting motion heartbeats',
        tone: 'warning',
        description: `${awaitingCameras} camera${awaitingCameras > 1 ? 's are' : ' is'} reachable but awaiting fresh motion to confirm the stream.`,
      };
    }
    return {
      label: 'No cameras online',
      tone: 'error',
      description: 'Bring at least one RTSP stream online to resume monitoring.',
    };
  }

  if (offlineCameras > 0) {
    return {
      label: 'Some cameras offline',
      tone: 'warning',
      description: `${offlineCameras} camera${offlineCameras > 1 ? 's are' : ' is'} unreachable. Check network or power.`,
    };
  }

  if (awaitingCameras > 0) {
    return {
      label: 'Awaiting motion confirmation',
      tone: 'warning',
      description: `${awaitingCameras} camera${awaitingCameras > 1 ? 's need' : ' needs'} motion to revalidate health.`,
    };
  }

  if (fallsToday > 0) {
    return {
      label: 'Incidents awaiting attention',
      tone: 'warning',
      description: `${fallsToday} fall${fallsToday > 1 ? 's' : ''} detected today. Review and acknowledge.`,
    };
  }

  if (openEnquiries > 5) {
    return {
      label: 'Inbox backlog rising',
      tone: 'warning',
      description: 'There are more than five open enquiries. Consider reassigning follow-ups.',
    };
  }

  return {
    label: 'All systems nominal',
    tone: 'success',
    description: 'Cameras, alerts, and notifications are operating normally.',
  };
};

const DashboardHero = ({ stats, navigate }) => {
  const systemStatus = useMemo(() => getSystemStatus(stats), [stats]);
  const toneToBadge = {
    success: { background: 'rgba(45, 212, 191, 0.2)', borderColor: 'rgba(45, 212, 191, 0.45)', color: '#F0FFFD' },
    warning: { background: 'rgba(253, 186, 116, 0.28)', borderColor: 'rgba(253, 186, 116, 0.5)', color: '#FFF8EF' },
    error: { background: 'rgba(248, 113, 113, 0.24)', borderColor: 'rgba(248, 113, 113, 0.48)', color: '#FFF5F5' },
    default: { background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.24)', color: 'inherit' },
  };
  const toneStyles = toneToBadge[systemStatus.tone] ?? toneToBadge.default;
  const badges = [
    {
      label: 'Operations Command Centre',
      icon: <TimelineIcon sx={{ color: 'primary.contrastText' }} />,
      borderColor: 'rgba(255,255,255,0.3)',
      background: 'rgba(255,255,255,0.14)',
      color: 'inherit',
    },
    {
      label: systemStatus.label,
      ...toneStyles,
    },
  ];

  const actions = [
    {
      label: 'Start Live Patrol',
      color: 'secondary',
      icon: <PlayArrowIcon />,
      onClick: () => navigate('/cameras'),
    },
    {
      label: 'Review Falls',
      variant: 'outlined',
      color: 'inherit',
      icon: <AssignmentTurnedInIcon />,
      onClick: () => navigate('/fall-events'),
    },
    {
      label: 'Contact Inbox',
      variant: 'outlined',
      color: 'inherit',
      icon: <AssessmentIcon />,
      onClick: () => navigate('/contact-submissions'),
    },
  ];

  const rightContent = (
    <Stack spacing={1.5} alignItems={{ xs: 'flex-start', lg: 'flex-end' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.5,
          py: 0.6,
          borderRadius: 1,
          border: '1px solid rgba(255,255,255,0.22)',
          bgcolor: 'rgba(255,255,255,0.12)',
        }}
      >
        <CalendarTodayIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {new Date().toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ maxWidth: 240, opacity: 0.85, textAlign: { xs: 'left', lg: 'right' } }}>
        Thank you for keeping residents safe. Take a moment to check in on anyone awaiting reassurance.
      </Typography>
    </Stack>
  );

  return (
    <HeroCard
      badges={badges}
      title={`Welcome back, ${stats?.current_user?.first_name || 'Team'}`}
      description={systemStatus.description}
      actions={actions}
      rightContent={rightContent}
      actionLayout="row"
    />
  );
};

export default DashboardHero;








